/**
 * Network switch catalog — PoE switches from TP-Link, Ubiquiti UniFi, Netgear, Mikrotik.
 *
 * All specs verified against official manufacturer product pages on 2026-05-14.
 * Source URL is cited above each entry. Missing specs are left undefined rather
 * than guessed. `verified: true` only when every populated spec was read on the
 * manufacturer page; `verified: false` when some specs are inferred from
 * partial store-page data (Ubiquiti store pages omit deep specs).
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const NETWORK_SWITCH_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // TP-Link — Omada SDN + standalone SMB switches
  // ─────────────────────────────────────────────────────────────

  // https://www.tp-link.com/us/business-networking/unmanaged-switch/tl-sg1008p/ (read 2026-05-14)
  {
    id: 'tp-link-tl-sg1008p',
    manufacturer: 'TP-Link',
    model: 'TL-SG1008P',
    category: 'poe-switch',
    tagline: '8-port Gigabit unmanaged, 4 PoE+ ports, 64 W budget — desktop SMB',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/unmanaged-switch/tl-sg1008p/',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 64,
      poeStandard: '802.3at',
      poePerPortW: 30,
      switchingCapacityGbps: 16,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.tp-link.com/us/business-networking/easy-smart-switch/tl-sg1218mpe/ (read 2026-05-14)
  {
    id: 'tp-link-tl-sg1218mpe',
    manufacturer: 'TP-Link',
    model: 'TL-SG1218MPE',
    category: 'poe-switch',
    tagline: '16-port Gigabit PoE+ Easy Smart, 250 W budget, 2 combo SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.tp-link.com/us/business-networking/easy-smart-switch/tl-sg1218mpe/',
    specs: {
      totalPorts: 18,
      poePorts: 16,
      poeBudgetW: 250,
      poeStandard: '802.3at',
      uplinks: '2× combo SFP',
      switchingCapacityGbps: 36,
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.omadanetworks.com/us/business-networking/omada-switch-access/tl-sg2210mp/ (read 2026-05-14)
  {
    id: 'tp-link-tl-sg2210mp',
    manufacturer: 'TP-Link',
    model: 'TL-SG2210MP',
    category: 'poe-switch',
    tagline: '8-port Gigabit Omada L2 managed, 150 W PoE+ budget, 2 SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access/tl-sg2210mp/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 150,
      poeStandard: '802.3at',
      uplinks: '2× SFP',
      switchingCapacityGbps: 20,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 50',
    },
  },

  // https://www.omadanetworks.com/us/business-networking/omada-switch-access-pro/tl-sg3210xhp-m2/ (read 2026-05-14)
  {
    id: 'tp-link-tl-sg3210xhp-m2',
    manufacturer: 'TP-Link',
    model: 'TL-SG3210XHP-M2',
    category: 'poe-switch',
    tagline: '8-port 2.5G PoE+ L2+ managed, 240 W budget, 2× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access-pro/tl-sg3210xhp-m2/',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 240,
      poeStandard: '802.3at',
      uplinks: '2× 10G SFP+',
      switchingCapacityGbps: 80,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '0 to 50',
    },
  },

  // https://www.omadanetworks.com/us/business-networking/omada-switch-access-plus/tl-sg3428xmp/ (read 2026-05-14)
  {
    id: 'tp-link-tl-sg3428xmp',
    manufacturer: 'TP-Link',
    model: 'TL-SG3428XMP',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ L2+ managed, 384 W budget, 4× 10G SFP+ uplinks',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/us/business-networking/omada-switch-access-plus/tl-sg3428xmp/',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 384,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 128,
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Ubiquiti UniFi — cloud-managed L2/L3 switches
  // NOTE: UniFi store pages publish only headline specs; deeper
  // specs (PoE standard, per-port W, SFP count) are not on the
  // store page and not in a public datasheet, so they are left
  // undefined and `verified: false`. Enterprise 24 PoE has a
  // complete Technical tab and is `verified: true`.
  // ─────────────────────────────────────────────────────────────

  // https://store.ui.com/us/en/products/usw-lite-8-poe (read 2026-05-14)
  {
    id: 'ubiquiti-usw-lite-8-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Lite-8-PoE',
    category: 'poe-switch',
    tagline: '8-port Gigabit L2 managed UniFi, 52 W PoE budget, fanless desktop',
    verified: false,
    datasheetUrl: 'https://store.ui.com/us/en/products/usw-lite-8-poe',
    specs: {
      totalPorts: 8,
      poePorts: 4,
      poeBudgetW: 52,
      poeStandard: '802.3at',
      management: 'L2-managed',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://store.ui.com/us/en/products/usw-24-poe (read 2026-05-14)
  {
    id: 'ubiquiti-usw-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-24-PoE',
    category: 'poe-switch',
    tagline: '24-port Gigabit L2 managed UniFi, 95 W PoE budget, fanless rack-1U',
    verified: false,
    datasheetUrl: 'https://store.ui.com/us/en/products/usw-24-poe',
    specs: {
      totalPorts: 24,
      poePorts: 16,
      poeBudgetW: 95,
      poeStandard: '802.3at',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // https://store.ui.com/us/en/products/usw-48-poe (read 2026-05-14)
  {
    id: 'ubiquiti-usw-48-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-48-PoE',
    category: 'poe-switch',
    tagline: '48-port Gigabit L2 managed UniFi, 195 W PoE budget, fanless rack-1U',
    verified: false,
    datasheetUrl: 'https://store.ui.com/us/en/products/usw-48-poe',
    specs: {
      totalPorts: 48,
      poePorts: 32,
      poeBudgetW: 195,
      poeStandard: '802.3at',
      management: 'L2-managed',
      formFactor: 'rack-1U',
      fanless: true,
    },
  },

  // https://store.ui.com/us/en/products/usw-pro-24-poe (read 2026-05-14)
  {
    id: 'ubiquiti-usw-pro-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Pro-24-PoE',
    category: 'poe-switch',
    tagline: '24-port Gigabit L3 managed UniFi Pro, 400 W PoE++ budget',
    verified: false,
    datasheetUrl: 'https://store.ui.com/us/en/products/usw-pro-24-poe',
    specs: {
      totalPorts: 24,
      poePorts: 24,
      poeBudgetW: 400,
      poeStandard: '802.3bt-Type3',
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  // https://store.ui.com/us/en/products/usw-enterprise-24-poe (read 2026-05-14)
  {
    id: 'ubiquiti-usw-enterprise-24-poe',
    manufacturer: 'Ubiquiti',
    model: 'USW-Enterprise-24-PoE',
    category: 'poe-switch',
    tagline: '24-port 2.5G PoE+ L3 managed UniFi Enterprise, 400 W budget, 2× 10G SFP+',
    verified: true,
    datasheetUrl: 'https://store.ui.com/us/en/products/usw-enterprise-24-poe',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 400,
      poeStandard: '802.3at',
      poePerPortW: 32,
      uplinks: '2× 10G SFP+',
      switchingCapacityGbps: 124,
      management: 'L3-managed',
      formFactor: 'rack-1U',
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Netgear — unmanaged + ProSAFE smart-managed
  // ─────────────────────────────────────────────────────────────

  // https://www.netgear.com/business/wired/switches/unmanaged/gs108pp/ (read 2026-05-14)
  {
    id: 'netgear-gs108pp',
    manufacturer: 'Netgear',
    model: 'GS108PP',
    category: 'poe-switch',
    tagline: '8-port Gigabit unmanaged, all 8 ports PoE+, 123 W FlexPoE budget',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/unmanaged/gs108pp/',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 123,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: false,
    },
  },

  // https://www.netgear.com/business/wired/switches/unmanaged/gs308pp/ (read 2026-05-14)
  {
    id: 'netgear-gs308pp',
    manufacturer: 'Netgear',
    model: 'GS308PP',
    category: 'poe-switch',
    tagline: '8-port Gigabit unmanaged, all 8 PoE+, 83 W budget, fanless desktop',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/unmanaged/gs308pp/',
    specs: {
      totalPorts: 8,
      poePorts: 8,
      poeBudgetW: 83,
      poeStandard: '802.3at',
      poePerPortW: 30,
      management: 'unmanaged',
      formFactor: 'desktop',
      fanless: true,
    },
  },

  // https://www.netgear.com/business/wired/switches/smart/gs324tpv2/ (read 2026-05-14)
  {
    id: 'netgear-gs324tpv2',
    manufacturer: 'Netgear',
    model: 'GS324TPv2',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ smart-managed, 190 W budget, 2 SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/smart/gs324tpv2/',
    specs: {
      totalPorts: 26,
      poePorts: 24,
      poeBudgetW: 190,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× SFP',
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // https://www.netgear.com/business/wired/switches/smart/gs348tpv2/ (read 2026-05-14)
  {
    id: 'netgear-gs348tpv2',
    manufacturer: 'Netgear',
    model: 'GS348TPv2',
    category: 'poe-switch',
    tagline: '48-port Gigabit PoE+ smart-managed, 380 W budget, 4 SFP uplinks',
    verified: true,
    datasheetUrl: 'https://www.netgear.com/business/wired/switches/smart/gs348tpv2/',
    specs: {
      totalPorts: 52,
      poePorts: 48,
      poeBudgetW: 380,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× SFP',
      management: 'smart-managed',
      formFactor: 'rack-1U',
      fanless: false,
    },
  },

  // ─────────────────────────────────────────────────────────────
  // Mikrotik — RouterOS + SwOS switches
  // ─────────────────────────────────────────────────────────────

  // https://mikrotik.com/product/crs112_8p_4s_in (read 2026-05-14)
  {
    id: 'mikrotik-crs112-8p-4s-in',
    manufacturer: 'Mikrotik',
    model: 'CRS112-8P-4S-IN',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE-out + 4 SFP, RouterOS L5, fanless desktop',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/crs112_8p_4s_in',
    specs: {
      totalPorts: 12,
      poePorts: 8,
      poeBudgetW: 67,
      poeStandard: '802.3at',
      uplinks: '4× SFP',
      switchingCapacityGbps: 24,
      management: 'L3-managed',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-20 to 60',
    },
  },

  // https://mikrotik.com/product/crs328_24p_4s_rm (read 2026-05-14)
  {
    id: 'mikrotik-crs328-24p-4s-rm',
    manufacturer: 'Mikrotik',
    model: 'CRS328-24P-4S+RM',
    category: 'poe-switch',
    tagline: '24-port Gigabit PoE+ + 4× 10G SFP+, RouterOS/SwOS, 450 W budget',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/crs328_24p_4s_rm',
    specs: {
      totalPorts: 28,
      poePorts: 24,
      poeBudgetW: 450,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '4× 10G SFP+',
      switchingCapacityGbps: 126,
      management: 'L3-managed',
      formFactor: 'rack-1U',
      fanless: false,
      operatingTempC: '-20 to 60',
    },
  },

  // https://mikrotik.com/product/css610_8p_2s_in (read 2026-05-14)
  {
    id: 'mikrotik-css610-8p-2s-in',
    manufacturer: 'Mikrotik',
    model: 'CSS610-8P-2S+IN',
    category: 'poe-switch',
    tagline: '8-port Gigabit PoE+ + 2× 10G SFP+, SwOS Lite, 140 W budget, fanless',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/css610_8p_2s_in',
    specs: {
      totalPorts: 10,
      poePorts: 8,
      poeBudgetW: 140,
      poeStandard: '802.3at',
      poePerPortW: 30,
      uplinks: '2× 10G SFP+',
      switchingCapacityGbps: 56,
      management: 'smart-managed',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-40 to 70',
    },
  },

  // https://mikrotik.com/product/RB960PGS (read 2026-05-14)
  // Note: official model name is hEX PoE (RB960PGS) — 5-port router with PoE-out.
  {
    id: 'mikrotik-rb960pgs',
    manufacturer: 'Mikrotik',
    model: 'RB960PGS (hEX PoE)',
    category: 'poe-switch',
    tagline: '5-port Gigabit + 1 SFP, 4× PoE-out, RouterOS L4, fanless desktop',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/RB960PGS',
    specs: {
      totalPorts: 6,
      poePorts: 4,
      poeBudgetW: 54,
      poeStandard: '802.3at',
      uplinks: '1× SFP',
      management: 'L3-managed',
      formFactor: 'desktop',
      fanless: true,
      operatingTempC: '-40 to 60',
    },
  },
];
